import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic2, ListMusic, ExternalLink } from "lucide-react";

const SONGLIST_URL = "https://docs.google.com/spreadsheets/d/1_cnZe7jwptpuuhRnrXiOZJbxxFsUcXHt/edit?usp=sharing&ouid=103916521800636976298&rtpof=true&sd=true";

const eventTypes = [
  "Wedding", "Private Party", "Corporate Party", "Public Venue", "Other",
];

const budgets = [
  "Under $1,500", "$1,500 – $3,000", "$3,000 – $5,000",
  "$5,000 – $10,000", "$10,000+", "Not sure yet",
];

const lengths = ["1 hour", "2 hours", "3 hours", "4 hours", "5+ hours"];

const empty = {
  name: "", email: "", phone: "", event_date: "", event_type: "",
  event_location: "", guest_count: "", budget_range: "",
  indoor_outdoor: "", performance_length: "",
  special_requests: "", heard_from: "",
};

const Book = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.event_date || !form.event_type || !form.event_location) {
      toast.error("Please fill the required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Inquiry sent. We'll be in touch within 48 hours.");
      setForm(empty);
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="book-success">
        <div className="text-center max-w-xl">
          <p className="eyebrow">Booking inquiry received</p>
          <h1 className="font-heading text-5xl md:text-6xl uppercase mt-4 leading-[0.95]">We got it.</h1>
          <p className="mt-6 text-neutral-300 text-lg">
            Expect a reply from us within 48 hours with availability, pricing, and next steps.
          </p>
          <button onClick={() => setDone(false)} className="btn-retro mt-10" data-testid="book-another">Send another</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="min-h-screen">
      <section className="pt-20 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <p className="eyebrow fade-up"><Mic2 size={12} className="inline mr-2" /> Hire Retrofit</p>
              <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.9] mt-5 fade-up-1" data-testid="book-title">
                Book the Band
              </h1>
            </div>
            <a
              href={SONGLIST_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-retro-solid fade-up-1 self-start md:self-center"
              data-testid="book-view-songlist"
            >
              <ListMusic size={14} className="mr-2" /> View Songlist <ExternalLink size={12} className="ml-2" />
            </a>
          </div>
          <p className="mt-6 max-w-2xl text-lg text-neutral-300 fade-up-2">
            Tell us about your event. We'll come back with availability, a setlist plan, and pricing — usually within 48 hours. Take a look at our full songlist to see what we play.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <form onSubmit={submit} className="space-y-10" data-testid="book-form">
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <label className="label-retro">Your name *</label>
                <input value={form.name} onChange={onChange("name")} className="field" required data-testid="bk-name" />
              </div>
              <div>
                <label className="label-retro">Email *</label>
                <input type="email" value={form.email} onChange={onChange("email")} className="field" required data-testid="bk-email" />
              </div>
              <div>
                <label className="label-retro">Phone *</label>
                <input value={form.phone} onChange={onChange("phone")} className="field" required data-testid="bk-phone" />
              </div>
              <div>
                <label className="label-retro">Event date *</label>
                <input type="date" value={form.event_date} onChange={onChange("event_date")} className="field [color-scheme:dark]" required data-testid="bk-date" />
              </div>
              <div>
                <label className="label-retro">Event type *</label>
                <select value={form.event_type} onChange={onChange("event_type")} className="field [color-scheme:dark]" required data-testid="bk-type">
                  <option value="" className="bg-black">Select event type</option>
                  {eventTypes.map((t) => <option key={t} className="bg-black">{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label-retro">Event location *</label>
                <input value={form.event_location} onChange={onChange("event_location")} className="field" placeholder="Venue or city" required data-testid="bk-location" />
              </div>
              <div>
                <label className="label-retro">Estimated guest count</label>
                <input value={form.guest_count} onChange={onChange("guest_count")} className="field" placeholder="e.g. 150" data-testid="bk-guests" />
              </div>
              <div>
                <label className="label-retro">Budget range</label>
                <select value={form.budget_range} onChange={onChange("budget_range")} className="field [color-scheme:dark]" data-testid="bk-budget">
                  <option value="" className="bg-black">Select budget</option>
                  {budgets.map((b) => <option key={b} className="bg-black">{b}</option>)}
                </select>
              </div>
              <div>
                <label className="label-retro">Indoor or outdoor</label>
                <select value={form.indoor_outdoor} onChange={onChange("indoor_outdoor")} className="field [color-scheme:dark]" data-testid="bk-venue-type">
                  <option value="" className="bg-black">Select</option>
                  <option className="bg-black">Indoor</option>
                  <option className="bg-black">Outdoor</option>
                  <option className="bg-black">Both</option>
                </select>
              </div>
              <div>
                <label className="label-retro">Desired performance length</label>
                <select value={form.performance_length} onChange={onChange("performance_length")} className="field [color-scheme:dark]" data-testid="bk-length">
                  <option value="" className="bg-black">Select length</option>
                  {lengths.map((l) => <option key={l} className="bg-black">{l}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="label-retro">Special requests</label>
              <textarea value={form.special_requests} onChange={onChange("special_requests")} className="field-area" placeholder="First dance song, must-play tracks, dress code, sound restrictions..." data-testid="bk-special" />
            </div>
            <div>
              <label className="label-retro">How did you hear about us?</label>
              <input value={form.heard_from} onChange={onChange("heard_from")} className="field" placeholder="Instagram, friend, saw us at..." data-testid="bk-heard" />
            </div>

            <div className="border-t border-white/10 pt-6 flex items-center justify-end flex-wrap gap-4">
              <button disabled={submitting} type="submit" className="btn-retro-solid disabled:opacity-50" data-testid="bk-submit">
                {submitting ? "Sending..." : "Submit Inquiry"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Book;
